import { yupResolver } from "@hookform/resolvers/yup";
import { Slider } from "@material-ui/core";
import useIsMounted from "ismounted";
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { Controller, useForm, useFormContext } from "react-hook-form";
import { object, reach } from "yup";
import { ErrorText } from "../../../components/billing";
import { FileField } from "../../../components/forms/file-3";
import { NumberField } from "../../../components/forms/number";
import { TextField } from "../../../components/forms/text";
import { Grid } from "../../../components/layout";
import { StepperContext } from "../../../components/stepper/context";
import {
  Container,
  FieldSection,
  FieldSubSection,
  SectionTitle,
} from "./common/components";
import { FormType } from "./common/form";
import { useDebouncedFormChange, useParentFormSchema } from "./hooks";

type ParentFormType = FormType;
type InnerFormType = {
  css: ParentFormType["configuration"]["css"];
  headerImage: ParentFormType["headerImage"];
};

export const Styling: React.FunctionComponent<{}> = () => {
  const { setValidation } = useContext(StepperContext);
  const isMounted = useIsMounted();

  const schema = useParentFormSchema();
  const resolver = useMemo(
    () =>
      object().shape({
        css: reach(schema, "configuration.css"),
        headerImage: reach(schema, "headerImage"),
      }),
    [schema]
  );

  const { setValue, getValues } = useFormContext<FormType>();

  const innerForm = useForm<InnerFormType>({
    resolver: yupResolver(resolver),
    defaultValues: useMemo(() => ({ css: getValues().configuration.css }), [
      getValues,
    ]),
  });

  const setOuterValues = useCallback(
    (x) => {
      setValue("configuration.css", x.css as any);
      setValue("headerImage", x.headerImage as any);
    },
    [setValue]
  );

  useDebouncedFormChange(innerForm, setOuterValues);

  /** Set up the validation for the form */
  useEffect(() => {
    setValidation(() => () =>
      new Promise<void>((res, rej) => {
        innerForm.handleSubmit((d) => {
          setOuterValues(d);
          res();
        })();
      })
    );

    return () => {
      if (isMounted.current) {
        // We have to check if it's mounted otherwise it will clear current validation
        // This is because the prior page's clear validation is run after the new page's setValidation
        setValidation(() => () => Promise.resolve());
      }
    };
  }, [
    setValidation,
    innerForm.handleSubmit,
    isMounted.current,
    setOuterValues,
  ]);

  return (
    <Container>
      <FieldSection>
        <SectionTitle>Header</SectionTitle>
        <FieldSubSection>
          <strong>Text Colour</strong>
          <Controller
            control={innerForm.control}
            name="css.headerTextColor"
            render={({ field, fieldState: { error } }) => (
              <TextField
                error={!!error}
                helperText={error?.message}
                fullWidth
                type="color"
                {...field}
                value={field.value || "#000000"}
              />
            )}
          />
        </FieldSubSection>
        <FieldSubSection>
          <strong>Background</strong>
          <Grid col-xs={2}>
            <span>Rotation</span>
            <Controller
              control={innerForm.control}
              name="css.headerBackgroundColor.degree"
              render={({ field, fieldState: { error } }) => (
                <NumberField
                  error={!!error}
                  helperText={error?.message}
                  fullWidth
                  {...field}
                />
              )}
            />
            <span>Start</span>
            <Controller
              control={innerForm.control}
              name="css.headerBackgroundColor.start"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  error={!!error}
                  helperText={error?.message}
                  fullWidth
                  type="color"
                  {...field}
                  value={field.value || "#000000"}
                />
              )}
            />
            <span>End</span>
            <Controller
              control={innerForm.control}
              name="css.headerBackgroundColor.end"
              render={({ field, fieldState: { error } }) => (
                <TextField
                  error={!!error}
                  helperText={error?.message}
                  fullWidth
                  type="color"
                  {...field}
                  value={field.value || "#000000"}
                />
              )}
            />
            <span>Start Alpha</span>
            <Controller
              control={innerForm.control}
              name="css.headerBackgroundColor.startAlpha"
              render={({ field, fieldState: { error } }) => (
                <>
                  <Slider
                    value={field.value || 0}
                    onChange={(_, value) => field.onChange(value)}
                    orientation="horizontal"
                    aria-labelledby="discrete-slider"
                    valueLabelDisplay="off"
                    step={1}
                    min={0}
                    max={100}
                  />
                  {error ? <ErrorText>{error.message}</ErrorText> : null}
                </>
              )}
            />
            <span>End Alpha</span>
            <Controller
              control={innerForm.control}
              name="css.headerBackgroundColor.endAlpha"
              render={({ field, fieldState: { error } }) => (
                <>
                  <Slider
                    value={field.value || 0}
                    onChange={(_, value) => field.onChange(value)}
                    orientation="horizontal"
                    aria-labelledby="discrete-slider"
                    valueLabelDisplay="off"
                    step={1}
                    min={0}
                    max={100}
                  />
                  {error ? <ErrorText>{error.message}</ErrorText> : null}
                </>
              )}
            />
          </Grid>
        </FieldSubSection>
        <FieldSubSection>
          <strong>Header Image</strong>
          <Controller
            control={innerForm.control}
            name="headerImage"
            render={({ field, fieldState: { error } }) => (
              <FileField
                {...field}
                error={!!error}
                helperText={error?.message}
                label="Image"
              />
            )}
          />
        </FieldSubSection>
      </FieldSection>
      <FieldSection>
        <SectionTitle>Form</SectionTitle>
        <FieldSubSection>
          <strong>Text Colour</strong>
          <Controller
            control={innerForm.control}
            name="css.textColor"
            render={({ field, fieldState: { error } }) => (
              <TextField
                error={!!error}
                helperText={error?.message}
                fullWidth
                type="color"
                {...field}
                value={field.value || "#000000"}
              />
            )}
          />
        </FieldSubSection>
        <FieldSubSection>
          <strong>Background Colour</strong>
          <Controller
            control={innerForm.control}
            name="css.formBackgroundColor"
            render={({ field, fieldState: { error } }) => (
              <TextField
                error={!!error}
                helperText={error?.message}
                fullWidth
                type="color"
                {...field}
                value={field.value || "#000000"}
              />
            )}
          />
        </FieldSubSection>
      </FieldSection>
      <FieldSection>
        <SectionTitle>Buttons</SectionTitle>
        <FieldSubSection>
          <strong>Colour 1</strong>
          <Controller
            control={innerForm.control}
            name="css.buttonPrimaryColor"
            render={({ field, fieldState: { error } }) => (
              <TextField
                error={!!error}
                helperText={error?.message}
                fullWidth
                type="color"
                {...field}
                value={field.value || "#000000"}
              />
            )}
          />
        </FieldSubSection>
        <FieldSubSection>
          <strong>Colour 2</strong>
          <Controller
            control={innerForm.control}
            name="css.buttonSecondaryColor"
            render={({ field, fieldState: { error } }) => (
              <TextField
                error={!!error}
                helperText={error?.message}
                fullWidth
                type="color"
                {...field}
                value={field.value || "#000000"}
              />
            )}
          />
        </FieldSubSection>
      </FieldSection>
    </Container>
  );
};
